/**
 * Result page. Renders the resolved outcome tier's blocks. For a disqualified
 * outcome with `render_dq_reasons`, the accumulated DQ reasons render either:
 *
 *   - INLINE at the position of a `{ type: "dq_reasons" }` block in tier.blocks
 *     (preferred — lets the config place the WHY between the intro and the
 *     "What you can still do" section)
 *   - or APPENDED at the end of the page (fallback — legacy behavior when no
 *     placeholder block is present)
 *
 * PLAN.md §8.5.
 */
import type { Block, Outcome, ResultTier } from "../types";
import { Blocks } from "./Blocks";

export interface ResultPageProps {
  tier: ResultTier | undefined;
  outcome: Outcome;
}

function DqReasonsCard({
  heading,
  reasons,
}: {
  heading: string | undefined;
  reasons: string[];
}) {
  return (
    <div class="fe-card fe-card--warning">
      {heading ? (
        <h2 class="fe-h fe-h2" style="margin-bottom:10px">
          {heading}
        </h2>
      ) : null}
      <ul class="fe-list">
        {reasons.map((reason) => (
          <li class="fe-list-check">
            <span>{reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ResultPage({ tier, outcome }: ResultPageProps) {
  if (!tier) {
    return (
      <div class="fe-fade fe-card">
        <h1 class="fe-h fe-h1">Thank you.</h1>
        <p class="fe-text">Your responses have been received.</p>
      </div>
    );
  }

  const showReasons = tier.render_dq_reasons && outcome.dq_reasons.length > 0;
  const heading = tier.dq_reasons_heading; // optional — render no heading if unset
  const hasPlaceholder = tier.blocks.some((b: Block) => b.type === "dq_reasons");

  return (
    <div class="fe-fade" style="display:flex;flex-direction:column;gap:18px">
      {tier.blocks.map((block, i) => {
        if (block.type === "dq_reasons") {
          if (!showReasons) return null;
          return <DqReasonsCard key={`dq-${i}`} heading={heading} reasons={outcome.dq_reasons} />;
        }
        return <Blocks key={i} blocks={[block]} />;
      })}

      {showReasons && !hasPlaceholder ? (
        <DqReasonsCard heading={heading} reasons={outcome.dq_reasons} />
      ) : null}
    </div>
  );
}
